import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Icon from '../ui/Icon';
import Drawer from '../ui/Drawer';
import ConfirmDialog from '../ui/ConfirmDialog';
import { ErrorState } from '../ui/States';
import { ApiError } from '../../api/client';
import { feedbackApi } from '../../api/feedback';
import { toDetailView } from './feedbackAdapter';
import { categoryMeta } from './feedbackMeta';
import { feedbackDetailKey } from './useFeedback';
import { Conversation, OriginalSubmission } from './TicketDetail';
import ReplyForm from './ReplyForm';

const errorMessage = (err) => (err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');

function PanelSkeleton() {
  return (
    <div className="flex flex-col gap-4 animate-pulse" role="status" aria-label="Loading ticket">
      <div className="h-48 rounded-xl bg-surface-container-low" />
      <div className="h-8 rounded-lg bg-surface-container-low" />
      <div className="h-40 rounded-lg bg-surface-container-low" />
    </div>
  );
}

/**
 * Ticket + reply panel for `?id`. A sticky card next to the table on xl screens, the
 * shared Drawer below that. `onRequestClose` lets the page confirm unsent text first.
 */
export default function TicketPanel({ id, wide, onRequestClose, onDirtyChange }) {
  const queryClient = useQueryClient();
  const key = feedbackDetailKey(id);
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => feedbackApi.getFeedback(id, { signal }),
    staleTime: 0,
    retry: (count, err) => (err?.status === 0 || err?.status >= 500) && count < 1,
  });
  const ticket = useMemo(() => (query.data ? toDetailView(query.data) : null), [query.data]);
  const [confirmClose, setConfirmClose] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const applyServerTicket = (feedback) => {
    queryClient.setQueryData(key, feedback);
    queryClient.invalidateQueries({ queryKey: ['feedback-list'] });
  };

  const category = useMutation({
    mutationFn: (value) => feedbackApi.updateFeedback(id, { category: value }),
    onMutate: async (value) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (old) => (old ? { ...old, category: value } : old));
      return { previous };
    },
    onSuccess: ({ feedback }) => {
      applyServerTicket(feedback);
      toast.success(`Category changed to ${categoryMeta(feedback.category).label}`);
    },
    onError: (err, _value, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      toast.error(errorMessage(err));
    },
  });

  const status = useMutation({
    mutationFn: (value) => feedbackApi.updateFeedback(id, { status: value }),
    onSuccess: ({ feedback }, value) => {
      applyServerTicket(feedback);
      setConfirmClose(false);
      toast.success(value === 'closed' ? `Ticket #${feedback.ticketNo} closed` : `Ticket #${feedback.ticketNo} reopened`);
    },
    onError: (err) => {
      setConfirmClose(false);
      toast.error(errorMessage(err));
    },
  });

  let body;
  if (query.error) {
    body =
      query.error.status === 404 ? (
        <ErrorState icon="search_off" title="Ticket not found" description="This feedback doesn't exist anymore." />
      ) : (
        <ErrorState title="Couldn't load this ticket" description={query.error.message} onRetry={() => query.refetch()} />
      );
  } else if (!ticket) {
    body = <PanelSkeleton />;
  } else {
    body = (
      <div className="flex flex-col gap-5">
        <OriginalSubmission
          ticket={ticket}
          savingCategory={category.isPending}
          savingStatus={status.isPending}
          onCategoryChange={(value) => value !== ticket.categoryKey && category.mutate(value)}
          onClose={() => setConfirmClose(true)}
          onReopen={() => status.mutate('pending')}
        />
        <Conversation
          replies={ticket.replies}
          onSendAgain={(reply) => setPrefill({ subject: reply.subject, body: reply.body, attachments: reply.attachments })}
        />
        <ReplyForm key={ticket.id} ticket={ticket} prefill={prefill} onUpdated={applyServerTicket} onDirtyChange={onDirtyChange} />
      </div>
    );
  }

  const to = ticket?.replyDefaults.to;
  const dialogs = (
    <ConfirmDialog
      open={confirmClose}
      tone="primary"
      title={`Close ticket #${ticket?.ticketNo}?`}
      description="It moves to Closed. You can reopen it later."
      confirmLabel="Close ticket"
      busy={status.isPending}
      onConfirm={() => status.mutate('closed')}
      onCancel={() => setConfirmClose(false)}
    />
  );

  if (!wide) {
    return (
      <>
        <Drawer open title="Reply to Customer" subtitle={to ? `To: ${to}` : undefined} onRequestClose={onRequestClose}>
          {body}
        </Drawer>
        {dialogs}
      </>
    );
  }

  return (
    <aside
      aria-labelledby="ticket-panel-title"
      className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs p-5 flex flex-col gap-4 sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto"
    >
      <header className="flex items-start justify-between gap-3 pb-4 border-b border-surface-container">
        <div className="flex items-start gap-3 min-w-0">
          <span className="w-10 h-10 rounded-lg bg-primary-container text-on-primary flex items-center justify-center shrink-0">
            <Icon name="forward_to_inbox" size={22} />
          </span>
          <div className="min-w-0">
            <h2 id="ticket-panel-title" className="text-lg font-bold text-primary leading-tight">
              Reply to Customer
            </h2>
            {to && (
              <p className="text-xs text-on-surface-variant truncate">
                To: <span className="font-data-mono font-semibold text-secondary">{to}</span>
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onRequestClose}
          aria-label="Close panel"
          className="p-1.5 rounded-lg text-on-surface-variant hover:bg-surface-container hover:text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
        >
          <Icon name="close" size={22} />
        </button>
      </header>
      {body}
      {dialogs}
    </aside>
  );
}
